import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import usePageTitle from "../hooks/usePageTitle";
import axios from "axios";
import { toast } from "sonner";
import { format, isValid, parseISO } from "date-fns";
import {
  Users,
  CreditCard,
  Store,
  UserPlus,
  BarChart3,
  LogOut,
  TrendingUp,
  Key,
  MessageSquare,
  Megaphone,
  LayoutDashboard
} from "lucide-react";

// Import tab components
import {
  DashboardTab,
  UsersTab,
  MembershipsTab,
  ApplicationsTab,
  BusinessesTab,
  AffiliatesTab,
  TerritoriesTab,
  TransactionsTab,
  AnalyticsTab,
  CrmTab,
  MarketingTab
} from "./admin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Permission mapping for tabs
const TAB_PERMISSIONS = {
  dashboard: null, // Everyone can see dashboard
  users: "users",
  memberships: "memberships",
  applications: "applications",
  businesses: "businesses",
  affiliates: "affiliates",
  territories: "territories",
  transactions: "sales",
  crm: "crm",
  marketing: "marketing",
  analytics: "analytics"
};

const AdminPage = () => {
  usePageTitle('Admin Panel', 'Advanta Plus administration dashboard');
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  // Core data state
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [businessApplications, setBusinessApplications] = useState([]);
  const [cardUsageAnalytics, setCardUsageAnalytics] = useState(null);
  const [subLocations, setSubLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // CRM state
  const [crmLeads, setCrmLeads] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  // Marketing state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailCampaigns, setEmailCampaigns] = useState([]);

  // UI states
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: "", new: "", confirm: "" });
  const [changingPassword, setChangingPassword] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploadingLeads, setUploadingLeads] = useState(false);
  const [converting, setConverting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Permission check helper
  const hasPermission = (permission) => {
    if (!permission) return true; // null permission means everyone can access
    if (user?.role === "admin") return true;
    if (user?.role === "territory_owner") return true;
    if (user?.role === "team_member") {
      return user?.permissions?.includes(permission);
    }
    return false;
  };

  const getDefaultTab = () => {
    return "dashboard"; // Dashboard is always the default tab now
  };

  // State for active tab (needed for navigation from dashboard)
  const [activeTab, setActiveTab] = useState(getDefaultTab());

  // Safe date formatter
  const safeFormat = (dateStr, formatStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
      return isValid(date) ? format(date, formatStr) : 'N/A';
    } catch {
      return 'N/A';
    }
  };

  useEffect(() => {
    seedData();
  }, []);

  useEffect(() => {
    if (user) fetchSubLocations();
  }, [user?.territory_id]);

  const fetchSubLocations = async () => {
    try {
      if (user?.role === "admin") {
        // Admin sees all territories' locations in one call
        const response = await axios.get(`${API}/sub-locations`);
        setSubLocations(response.data);
      } else {
        const tid = user?.territory_id || "costa_del_sol";
        const response = await axios.get(`${API}/sub-locations?territory_id=${tid}`);
        setSubLocations(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch sub-locations:", error);
    }
  };

  const seedData = async () => {
    // /seed now requires admin (tightened in fa79af0). For non-admin roles —
    // team_member, territory_owner — calling /seed used to return 200 silently
    // but now 401s when called without auth, which trips the response
    // interceptor and force-logs the user back out to /login. Skip the call
    // for non-admin users and just load their data.
    if (user?.role !== "admin") {
      fetchAllData();
      return;
    }
    try {
      await axios.post(`${API}/seed`, {}, getAuthHeader());
      fetchAllData();
    } catch (error) {
      console.error("Seed error:", error);
      fetchAllData();
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const isAdmin = user?.role === "admin";
      const isTeamMember = user?.role === "team_member";
      const isTerritoryOwner = user?.role === "territory_owner";

      if (isAdmin || isTeamMember) {
        // Full admin access
        const [statsRes, usersRes, membershipsRes, businessesRes, affiliatesRes, territoriesRes, transactionsRes, applicationsRes, analyticsRes, crmLeadsRes, templatesRes, campaignsRes, teamRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, headers).catch(() => ({ data: null })),
          axios.get(`${API}/admin/users`, headers).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/memberships`, headers).catch(() => ({ data: [] })),
          axios.get(`${API}/businesses/all?territory_id=${user?.territory_id || "costa_del_sol"}`, headers).catch(() => ({ data: [] })),
          axios.get(`${API}/affiliates`, headers).catch(() => ({ data: [] })),
          axios.get(`${API}/territories`).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/transactions`, headers).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/business-applications`, headers).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/analytics/card-usage?days=30`, headers).catch(() => ({ data: null })),
          axios.get(`${API}/admin/crm/leads`, headers).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/email/templates`, headers).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/email/campaigns`, headers).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/team`, headers).catch(() => ({ data: [] }))
        ]);

        setStats(statsRes.data);
        setUsers(usersRes.data);
        setMemberships(membershipsRes.data);
        setBusinesses(businessesRes.data);
        setAffiliates(affiliatesRes.data);
        setTerritories(territoriesRes.data);
        setTransactions(transactionsRes.data);
        setBusinessApplications(applicationsRes.data);
        setCardUsageAnalytics(analyticsRes.data);
        setCrmLeads(crmLeadsRes.data || []);
        setEmailTemplates(templatesRes.data || []);
        setEmailCampaigns(campaignsRes.data || []);
        setTeamMembers(teamRes.data || []);
      } else if (isTerritoryOwner) {
        // Territory owner - filtered access
        const [statsRes, usersRes, businessesRes, territoriesRes] = await Promise.all([
          axios.get(`${API}/admin/territory/stats`, headers).catch(() => ({ data: null })),
          axios.get(`${API}/admin/territory/users`, headers).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/territory/businesses`, headers).catch(() => ({ data: [] })),
          axios.get(`${API}/territories`).catch(() => ({ data: [] }))
        ]);

        setStats(statsRes.data);
        setUsers(usersRes.data);
        setBusinesses(businessesRes.data);
        setTerritories(territoriesRes.data);
        setMemberships([]);
        setAffiliates([]);
        setTransactions([]);
        setBusinessApplications([]);
        setCardUsageAnalytics(null);
        setCrmLeads([]);
        setEmailTemplates([]);
        setEmailCampaigns([]);
        setTeamMembers([]);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleChangePassword = async () => {
    if (passwordData.new !== passwordData.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (passwordData.new.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current,
        new_password: passwordData.new
      }, getAuthHeader());
      toast.success("Password changed successfully!");
      setShowPasswordDialog(false);
      setPasswordData({ current: "", new: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  // ==================== HANDLER FUNCTIONS ====================

  // User handlers
  const handleDeleteUser = async (userId, userEmail) => {
    if (!confirm(`Are you sure you want to delete ${userEmail}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, getAuthHeader());
      toast.success(`User ${userEmail} deleted!`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  // Application handlers
  const handleApproveApplication = async (appId) => {
    try {
      await axios.post(`${API}/admin/business-applications/${appId}/approve`, {}, getAuthHeader());
      toast.success("Business approved and created!");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const handleRejectApplication = async (appId) => {
    if (!confirm("Are you sure you want to reject this application?")) return;
    try {
      await axios.post(`${API}/admin/business-applications/${appId}/reject`, {}, getAuthHeader());
      toast.success("Application rejected");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to reject");
    }
  };

  const handleDeleteApplication = async (appId) => {
    try {
      await axios.delete(`${API}/admin/business-applications/${appId}`, getAuthHeader());
      toast.success("Application deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const handleEmailContract = async (app) => {
    try {
      await axios.post(`${API}/admin/business-applications/${app.id}/email-contract`, {}, getAuthHeader());
      toast.success(`Contract emailed to ${app.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  // Business handlers
  const handleCreateBusiness = async (businessData, editingBusiness, signedPdfBlob) => {
    try {
      if (editingBusiness) {
        await axios.put(`${API}/businesses/${editingBusiness.id}`, businessData, getAuthHeader());
        toast.success("Business updated!");
      } else {
        // Create business
        const res = await axios.post(`${API}/businesses`, businessData, getAuthHeader());
        const businessId = res.data?.id;

        // Upload signed agreement PDF if provided
        if (signedPdfBlob && businessId) {
          const formData = new FormData();
          formData.append("file", signedPdfBlob, `agreement_${businessData.name.replace(/\s+/g, '_')}.pdf`);
          const headers = { ...getAuthHeader().headers, "Content-Type": "multipart/form-data" };
          await axios.post(`${API}/businesses/${businessId}/agreement`, formData, { headers }).catch(() => {
            // Agreement upload is best-effort — business is already created
            console.warn("Agreement upload failed, but business was created");
          });
        }
        toast.success("Business created with signed agreement!");
      }
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save business");
    }
  };

  const handleDeleteBusiness = async (id) => {
    if (!confirm("Are you sure you want to delete this business?")) return;
    try {
      await axios.delete(`${API}/businesses/${id}`, getAuthHeader());
      toast.success("Business deleted!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete business");
    }
  };

  const handleAIExtract = async (url) => {
    if (!url) {
      toast.error("Please enter a website URL");
      return null;
    }
    setExtracting(true);
    try {
      const response = await axios.post(`${API}/ai/extract-business`, { url }, getAuthHeader());
      if (response.data.success && response.data.data) {
        toast.success("Business details extracted!");
        return response.data.data;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extract business details");
    } finally {
      setExtracting(false);
    }
    return null;
  };

  const handleGeocode = async (address) => {
    if (!address) {
      toast.error("Please enter an address first");
      return null;
    }
    try {
      const response = await axios.post(`${API}/ai/geocode`, { address }, getAuthHeader());
      if (response.data.latitude && response.data.longitude) {
        toast.success("Location found on map!");
        return { latitude: response.data.latitude, longitude: response.data.longitude };
      } else {
        toast.error("Couldn't find that address — paste a Google Maps link or 'lat,lng' below to set the pin manually");
      }
    } catch (error) {
      toast.error("Geocoding failed — paste a Google Maps link or coordinates below instead");
    }
    return null;
  };

  // Affiliate handlers
  const handleCreateAffiliate = async (affiliateData, signedPdfBlob) => {
    try {
      const res = await axios.post(`${API}/affiliates`, affiliateData, getAuthHeader());
      const affiliateId = res.data?.id;

      // Upload signed agreement PDF if provided
      if (signedPdfBlob && affiliateId) {
        const formData = new FormData();
        formData.append("file", signedPdfBlob, `affiliate_agreement_${affiliateData.name.replace(/\s+/g, '_')}.pdf`);
        const headers = { ...getAuthHeader().headers, "Content-Type": "multipart/form-data" };
        await axios.post(`${API}/affiliates/${affiliateId}/agreement`, formData, { headers }).catch(() => {
          console.warn("Agreement upload failed, but affiliate was created");
        });
      }
      toast.success("Affiliate created with signed agreement!");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create affiliate");
    }
  };

  // Territory handlers
  const handleCreateTerritory = async (territoryData) => {
    try {
      await axios.post(`${API}/territories`, territoryData, getAuthHeader());
      toast.success("Territory created!");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create territory");
    }
  };

  // CRM handlers
  const handleCreateLead = async (leadData) => {
    try {
      await axios.post(`${API}/admin/crm/leads`, leadData, getAuthHeader());
      toast.success("Lead created!");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create lead");
    }
  };

  const handleUpdateLead = async (leadId, leadData) => {
    try {
      await axios.put(`${API}/admin/crm/leads/${leadId}`, leadData, getAuthHeader());
      toast.success("Lead updated!");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update lead");
    }
  };

  const handleDeleteLead = async (leadId) => {
    try {
      await axios.delete(`${API}/admin/crm/leads/${leadId}`, getAuthHeader());
      toast.success("Lead deleted!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const handleAddNote = async (leadId, noteText) => {
    try {
      await axios.post(`${API}/admin/crm/leads/${leadId}/notes`, { text: noteText }, getAuthHeader());
      toast.success("Note added!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  const handleUpdateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/admin/crm/leads/${leadId}`, { status: newStatus }, getAuthHeader());
      toast.success("Status updated!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleConvertLead = async (lead, convertTo, convertData, signedPdfBlob) => {
    setConverting(true);
    try {
      if (convertTo === "business") {
        const formData = new FormData();
        formData.append("business_name", lead.company || lead.name);
        formData.append("contact_name", lead.name);
        formData.append("email", lead.email || "");
        formData.append("phone", lead.phone || "");
        formData.append("description", convertData.description);
        formData.append("address", convertData.address);
        formData.append("category", convertData.category);
        formData.append("discount_level", convertData.discount_level.toString());
        formData.append("sub_location", convertData.sub_location);
        formData.append("website", convertData.website);
        formData.append("converted_from_lead", lead.id);
        if (convertData.latitude) formData.append("latitude", convertData.latitude.toString());
        if (convertData.longitude) formData.append("longitude", convertData.longitude.toString());
        if (signedPdfBlob) formData.append("signed_agreement", signedPdfBlob, "partner_agreement.pdf");

        await axios.post(`${API}/business-applications`, formData, {
          headers: { ...getAuthHeader().headers, "Content-Type": "multipart/form-data" }
        });
        await axios.put(`${API}/admin/crm/leads/${lead.id}`, { status: "converted", converted_to: "business_application" }, getAuthHeader());
        toast.success("Business application created with signed agreement!");
      } else {
        const response = await axios.post(`${API}/admin/crm/leads/${lead.id}/convert`, { convert_to: "affiliate" }, getAuthHeader());
        if (response.data.success) {
          toast.success(`Lead converted to affiliate! Code: ${response.data.affiliate_code}`);
        }
      }
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert lead");
    } finally {
      setConverting(false);
    }
  };

  // Team member handlers
  const handleCreateTeamMember = async (memberData) => {
    try {
      await axios.post(`${API}/admin/team`, memberData, getAuthHeader());
      toast.success(memberData.send_invite ? "Team member added and invite sent!" : "Team member added!");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save team member");
    }
  };

  const handleEditTeamMember = async (memberId, memberData) => {
    try {
      await axios.put(`${API}/admin/team/${memberId}`, memberData, getAuthHeader());
      toast.success("Team member updated!");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update team member");
    }
  };

  const handleDeleteTeamMember = async (memberId) => {
    if (!confirm("Are you sure? This will deactivate their account.")) return;
    try {
      await axios.delete(`${API}/admin/team/${memberId}`, getAuthHeader());
      toast.success("Team member removed");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete team member");
    }
  };

  const handleResendInvite = async (memberId) => {
    try {
      await axios.post(`${API}/admin/team/${memberId}/resend-invite`, {}, getAuthHeader());
      toast.success("Invite email resent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invite");
    }
  };

  const handleAssignLead = async (leadId, memberId) => {
    try {
      await axios.post(`${API}/admin/crm/leads/${leadId}/assign`, { member_id: memberId }, getAuthHeader());
      toast.success(memberId ? "Lead assigned!" : "Lead unassigned");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to assign lead");
    }
  };

  const handleUploadLeads = async (file) => {
    if (!file.name.endsWith('.xls') && !file.name.endsWith('.xlsx')) {
      toast.error("Please upload an .xls or .xlsx file");
      return null;
    }
    setUploadingLeads(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/admin/crm/upload`, formData, {
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        toast.success(`Imported ${response.data.created} leads!`);
        fetchAllData();
        return response.data;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploadingLeads(false);
    }
    return null;
  };

  // Marketing handlers
  const handleGenerateContent = async (params) => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/admin/marketing/generate`, params, getAuthHeader());
      if (response.data.success) {
        toast.success("Content generated!");
        return response.data.content;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate content");
    } finally {
      setGenerating(false);
    }
    return null;
  };

  const handleSendEmail = async (emailData) => {
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/admin/email/send`, emailData, getAuthHeader());
      if (response.data.success) {
        toast.success(`Email sent to ${response.data.sent} recipients!`);
        fetchAllData();
        return true;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
    return false;
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      await axios.post(`${API}/admin/email/templates`, templateData, getAuthHeader());
      toast.success("Template saved!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save template");
    }
  };

  const handleLoadTemplate = (template) => {
    toast.success("Template loaded");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Navigation */}
      <nav className="bg-[#0B0F19] border-b border-[#D4AF37]/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <span className="text-[#D4AF37] text-sm font-medium">Admin Panel</span>
            <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
              <DialogTrigger asChild>
                <Button variant="ghost" className="text-white/60 hover:text-white" title="Change Password">
                  <Key className="w-5 h-5" />
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0B0F19] border-[#D4AF37]/20 text-white">
                <DialogHeader>
                  <DialogTitle className="font-['Playfair_Display'] text-xl flex items-center gap-2">
                    <Key className="w-5 h-5 text-[#D4AF37]" />
                    Change Password
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-white/70">Current Password</Label>
                    <Input
                      type="password"
                      value={passwordData.current}
                      onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      placeholder="Enter current password"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">New Password</Label>
                    <Input
                      type="password"
                      value={passwordData.new}
                      onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      placeholder="Enter new password (min 6 characters)"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Confirm New Password</Label>
                    <Input
                      type="password"
                      value={passwordData.confirm}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      placeholder="Confirm new password"
                    />
                  </div>
                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword}
                    className="btn-gold w-full"
                  >
                    {changingPassword ? "Changing..." : "Change Password"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button variant="ghost" onClick={handleLogout} className="text-white/60 hover:text-white" data-testid="admin-logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8" data-testid="admin-stats">
          {[
            { label: "Users", value: stats?.total_users || 0, icon: Users },
            { label: "Memberships", value: stats?.total_memberships || 0, icon: CreditCard },
            { label: "Active", value: stats?.active_memberships || 0, icon: CreditCard },
            { label: "Businesses", value: stats?.total_businesses || 0, icon: Store },
            { label: "Affiliates", value: stats?.total_affiliates || 0, icon: UserPlus },
            { label: "Revenue", value: `€${stats?.total_revenue?.toFixed(2) || 0}`, icon: BarChart3 },
            { label: "Commission", value: `€${stats?.total_commission?.toFixed(2) || 0}`, icon: BarChart3 },
          ].map((stat, index) => (
            <div key={index} className="bg-[#0B0F19] rounded-xl p-4 border border-[#D4AF37]/10">
              <stat.icon className="w-5 h-5 text-[#D4AF37] mb-2" />
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-white/50 text-xs">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#0B0F19] border border-[#D4AF37]/10 p-1 flex-wrap">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617]">
              <LayoutDashboard className="w-4 h-4 mr-1" />
              Dashboard
            </TabsTrigger>
            {hasPermission("users") && (
              <TabsTrigger value="users" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617]">Users</TabsTrigger>
            )}
            {hasPermission("memberships") && (
              <TabsTrigger value="memberships" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617]">Memberships</TabsTrigger>
            )}
            {hasPermission("applications") && (
              <TabsTrigger value="applications" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617] relative">
                Applications
                {businessApplications.filter(a => a.status === 'pending').length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {businessApplications.filter(a => a.status === 'pending').length}
                  </span>
                )}
              </TabsTrigger>
            )}
            {hasPermission("businesses") && (
              <TabsTrigger value="businesses" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617]">Businesses</TabsTrigger>
            )}
            {hasPermission("affiliates") && (
              <TabsTrigger value="affiliates" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617]">Affiliates</TabsTrigger>
            )}
            {hasPermission("territories") && (
              <TabsTrigger value="territories" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617]">Territories</TabsTrigger>
            )}
            {hasPermission("sales") && (
              <TabsTrigger value="transactions" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617]">Sales</TabsTrigger>
            )}
            {hasPermission("crm") && (
              <TabsTrigger value="crm" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617] relative">
                <MessageSquare className="w-4 h-4 mr-1" />
                CRM
                {crmLeads.filter(l => l.status === 'new').length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                    {crmLeads.filter(l => l.status === 'new').length}
                  </span>
                )}
              </TabsTrigger>
            )}
            {hasPermission("marketing") && (
              <TabsTrigger value="marketing" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617]">
                <Megaphone className="w-4 h-4 mr-1" />
                Marketing
              </TabsTrigger>
            )}
            {hasPermission("analytics") && (
              <TabsTrigger value="analytics" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#020617]">
                <TrendingUp className="w-4 h-4 mr-1" />
                Analytics
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="dashboard">
            <DashboardTab
              stats={stats}
              users={users}
              businesses={businesses}
              memberships={memberships}
              businessApplications={businessApplications}
              crmLeads={crmLeads}
              transactions={transactions}
              safeFormat={safeFormat}
              onNavigateToTab={setActiveTab}
              userRole={user?.role}
            />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab
              users={users}
              currentUserId={user?.id}
              safeFormat={safeFormat}
              onDeleteUser={handleDeleteUser}
            />
          </TabsContent>

          <TabsContent value="memberships">
            <MembershipsTab memberships={memberships} safeFormat={safeFormat} />
          </TabsContent>

          <TabsContent value="applications">
            <ApplicationsTab
              businessApplications={businessApplications}
              subLocations={subLocations}
              onApprove={handleApproveApplication}
              onReject={handleRejectApplication}
              onDelete={handleDeleteApplication}
              onEmailContract={handleEmailContract}
              onSaved={(status) => {
                toast.success(status === "pending" ? "Application submitted for review" : "Draft updated");
                fetchAllData();
              }}
              getAuthHeader={getAuthHeader}
            />
          </TabsContent>

          <TabsContent value="businesses">
            <BusinessesTab
              businesses={businesses}
              subLocations={subLocations}
              territories={territories}
              currentUser={user}
              onCreateBusiness={handleCreateBusiness}
              onEditBusiness={handleCreateBusiness}
              onDeleteBusiness={handleDeleteBusiness}
              onAIExtract={handleAIExtract}
              onGeocode={handleGeocode}
              extracting={extracting}
            />
          </TabsContent>

          <TabsContent value="affiliates">
            <AffiliatesTab affiliates={affiliates} onCreateAffiliate={handleCreateAffiliate} />
          </TabsContent>

          <TabsContent value="territories">
            <TerritoriesTab territories={territories} onCreateTerritory={handleCreateTerritory} />
          </TabsContent>

          <TabsContent value="transactions">
            <TransactionsTab transactions={transactions} safeFormat={safeFormat} />
          </TabsContent>

          <TabsContent value="crm">
            <CrmTab
              crmLeads={crmLeads}
              teamMembers={teamMembers}
              subLocations={subLocations}
              onCreateLead={handleCreateLead}
              onUpdateLead={handleUpdateLead}
              onDeleteLead={handleDeleteLead}
              onAddNote={handleAddNote}
              onUpdateLeadStatus={handleUpdateLeadStatus}
              onConvertLead={handleConvertLead}
              onCreateTeamMember={handleCreateTeamMember}
              onEditTeamMember={handleEditTeamMember}
              onDeleteTeamMember={handleDeleteTeamMember}
              onResendInvite={handleResendInvite}
              onAssignLead={handleAssignLead}
              onUploadLeads={handleUploadLeads}
              onAIExtractForConvert={handleAIExtract}
              uploadingLeads={uploadingLeads}
              converting={converting}
            />
          </TabsContent>

          <TabsContent value="marketing">
            <MarketingTab
              businesses={businesses}
              users={users}
              crmLeads={crmLeads}
              emailTemplates={emailTemplates}
              emailCampaigns={emailCampaigns}
              onGenerateContent={handleGenerateContent}
              onSendEmail={handleSendEmail}
              onSaveTemplate={handleSaveTemplate}
              onLoadTemplate={handleLoadTemplate}
              generating={generating}
              sendingEmail={sendingEmail}
            />
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsTab cardUsageAnalytics={cardUsageAnalytics} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
